import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, CircularProgress, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import React from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { buildValuesTab } from '../WidgetEditDialog/buildValuesTab';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getValueKey, getVisibleError, handleWidgetUpdate, joinLabels, loadValuesIfNeeded, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormAdvancedRadio = (props) => {
    var _a, _b, _c, _d;
    const data = props.getData();
    const values = React.useMemo(() => { var _a, _b; return (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : []; }, [props.config.values, props.internalData.values]);
    const error = getVisibleError(props);
    const selectedRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const formLabelSuffix = getFormLabelSuffix(props);
    const readOnly = props.config.readOnly || ((_a = props.rootParams.formParams) === null || _a === void 0 ? void 0 : _a.readOnly);
    const ariaLabelledBy = `radio-title-${props.id}`;
    React.useEffect(() => {
        if (selectedRef.current && containerRef.current) {
            scrollIntoView(selectedRef.current, { scrollMode: 'if-needed', boundary: containerRef.current });
        }
    }, [values]);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, error: !!error, disabled: props.config.disabled },
        props.config.label && (React.createElement(FormLabel, { id: ariaLabelledBy, sx: { mb: 2 }, error: !!error, disabled: props.config.disabled }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        props.internalData.loading && React.createElement(CircularProgress, { color: 'inherit', size: 20 }),
        React.createElement(Box, { ref: containerRef, sx: {
                maxHeight: (_b = props.config.style) === null || _b === void 0 ? void 0 : _b.maxHeight,
                overflow: 'auto',
                border: ((_c = props.config.style) === null || _c === void 0 ? void 0 : _c.showBorder) ? `1px solid ${themeColors.border.main}` : undefined,
            } },
            React.createElement(FormTooltip, { tooltip: props.config.tooltip },
                React.createElement(FormGroup, { sx: {
                        display: 'flex',
                        flexGrow: 1,
                        p: ((_d = props.config.style) === null || _d === void 0 ? void 0 : _d.showBorder) ? 2 : 0,
                    } },
                    React.createElement(RadioGroup, { sx: {
                            display: 'flex',
                            flexGrow: 1,
                            flexDirection: 'column',
                            gap: 2,
                        } }, values.map((value, index) => {
                        var _a;
                        const checked = props.config.valueEquals
                            ? props.config.valueEquals(data, value.value)
                            : data === value.value;
                        return (React.createElement(FormControlLabel, { ref: checked ? selectedRef : undefined, key: getValueKey(value.value), disabled: value.disabled, sx: {
                                m: 0,
                                p: 1,
                                backgroundColor: themeColors.surface.secondary,
                                cursor: readOnly ? 'default' : undefined,
                            }, control: React.createElement(Radio, { value: value.value, tabIndex: props.editMode ? -1 : undefined, disableTouchRipple: readOnly, checked: checked, onChange: () => {
                                    if (readOnly) {
                                        return;
                                    }
                                    props.setData(value.value);
                                    renderForm(props, {
                                        changed: true,
                                        autoSaveDebounce: props.config.autoSave,
                                    });
                                }, inputProps: {
                                    readOnly: readOnly,
                                    'aria-describedby': ariaDescribedBy,
                                }, sx: readOnly ? { cursor: 'default' } : undefined, autoFocus: ((_a = (!props.editMode && props.config.autoFocus)) !== null && _a !== void 0 ? _a : false) && index === 0, "aria-labelledby": ariaLabelledBy }), label: React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
                                React.createElement(Typography, { variant: 'subtitle2' }, value.label),
                                value.description && (React.createElement(Typography, { variant: 'body2', color: value.disabled ? themeColors.text.disable : themeColors.text.subtle }, value.description))) }));
                    }))))),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormAdvancedRadioEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Select: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            readOnly: true,
            tooltip: true,
            children: [
                {
                    type: 'form.select',
                    key: 'defaultValue',
                    label: 'Default Value',
                    values: props.config.values,
                    valueProvider: props.config.valueProvider,
                },
            ],
            autoFocus: true,
        }), buildValuesTab(props, { description: 'always' }), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.number',
                    key: 'style.maxHeight',
                    label: 'Max Height',
                    style: {
                        width: 200,
                    },
                },
                {
                    type: 'form.checkbox',
                    key: 'style.showBorder',
                    label: 'Show Border',
                },
            ],
        }), buildEventTab(props, { eventOnChange: true })) }),
        React.createElement(WidgetFormAdvancedRadio, Object.assign({}, props))));
};
export const widgetFormAdvancedRadio = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setData(props.config.defaultValue);
        }
        props.internalData.loadValues = true;
        loadValuesIfNeeded(props, false, false);
    },
    validate: (props) => {
        var _a, _b, _c, _d;
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value == null) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if ((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_d = props.config.validate.uniqueInListMessage) !== null && _d !== void 0 ? _d : error;
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        loadValuesIfNeeded(props, false, false);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b, _c;
        const data = props.getData();
        if (data) {
            const configValues = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), (_c = configValues.find((configValue) => configValue.value === data)) === null || _c === void 0 ? void 0 : _c.label);
        }
        return [];
    },
    component: WidgetFormAdvancedRadio,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormAdvancedRadioEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'advancedRadio', label: 'Advanced Radio', values: [] }),
};
